// Question type mapping from API to internal format
const QUESTION_TYPE_MAP: Record<string, string> = {
  text: 'text',
  short_text: 'text',
  long_text: 'long_text',
  textarea: 'long_text',
  single_choice: 'single_choice',
  radio: 'single_choice',
  multiple_choice: 'multiple_choice',
  checkbox: 'multiple_choice',
  dropdown: 'dropdown',
  select: 'dropdown',
  number: 'number',
  numeric: 'number',
  integer: 'number',
  yes_no: 'yes_no',
  yes_no_conditional: 'yes_no_conditional',
  boolean: 'yes_no',
  date: 'date',
  time: 'time',
  datetime: 'datetime',
  rating: 'rating',
  star_rating: 'star_rating',
  stars: 'star_rating',
  slider: 'slider',
  scale: 'slider',
  file_upload: 'file_upload',
  file: 'file_upload',
  signature: 'signature',
  location: 'location',
  address: 'location',
  geo: 'location',
  gps_auto: 'location',
}

export interface AggregatedQuestion {
  id: string
  aggregated_question_id: string
  code: string
  question_type: string
  type?: string // legacy field
  text?: string
  text_ar?: string
  text_translations?: {
    en: string
    ar: string
  }
  required: boolean
  order: number
  position?: number // legacy field
  options?: Array<{
    value: any
    label: string
    /** Some snapshots use split fields like the registration survey API */
    label_en?: string
    label_ar?: string
    /** Nested text object (alternate backend shape) */
    text?: { en?: string; ar?: string }
    label_translations?: {
      en: string
      ar: string
    }
  }>
  applies_to?: Array<{
    scope: string
    target_id: string
  }>
  is_phi?: boolean
  is_pii?: boolean
  aggregatable?: boolean
  canonical_key?: string | null
  validation_rules?: {
    pattern?: string
    [key: string]: unknown
  }
}

export interface LogicRule {
  trigger_question_id: string
  condition: string
  value: any
  action: string
  target_question_id: string
}

export interface EvaluationSnapshot {
  id: string
  name?: string
  name_en?: string
  name_ar?: string
  title?: string
  title_en?: string
  title_ar?: string
  survey_name?: string
  template_name?: string
  snapshot_data: {
    name?: string
    name_en?: string
    name_ar?: string
    title?: string
    survey_name?: string
    aggregated_questions: AggregatedQuestion[]
    logic_rules: LogicRule[]
    mapping_table: any[]
  }
}

/**
 * Appointment context from the order creation store,
 * used to enrich section headers with scheduled dates.
 */
export interface AppointmentContext {
  appointmentSpecId: string
  scheduledStart: string
  label: string
}

/**
 * Check if an evaluation snapshot has any questions
 * @param snapshot - The evaluation snapshot to check
 * @returns true if snapshot has questions, false otherwise
 */
export function hasQuestions(snapshot: EvaluationSnapshot | null | undefined): boolean {
  if (!snapshot) return false
  if (!snapshot.snapshot_data) return false
  if (!snapshot.snapshot_data.aggregated_questions) return false
  return snapshot.snapshot_data.aggregated_questions.length > 0
}

// ---------------------------------------------------------------------------
// TransformedQuestion & SurveyConfig
// ---------------------------------------------------------------------------

export interface TransformedQuestion {
  id: string
  code: string
  question_type: string
  position: number
  page_number: number
  text_translations: {
    en: string
    ar: string
  }
  validation_rules: {
    required: boolean
    pattern?: string | null
  }
  options?: Array<{
    id: string
    value: string
    label_translations: {
      en: string
      ar: string
    }
    position: number
  }>
  conditional_logic: Array<{
    question_id: string
    operator: string
    value: any
  }> | null
  // Section metadata — set on the FIRST question of each section group
  section_type?: 'general' | 'service'
  section_title_translations?: { en: string; ar: string }
  appointment_section_number?: number
  appointment_scheduled_at?: string
}

export interface SurveyConfig {
  version: number
  template_id: string
  survey_type: string
  show_progress: boolean
  allow_page_navigation: boolean
  name_translations: {
    en: string
    ar: string
  }
  questions: TransformedQuestion[]
}

// ---------------------------------------------------------------------------
// Option labels — backend may put internal keys in `label` / `label_translations.en`
// while real English lives in `label_en` (same pattern as registration surveys).
// ---------------------------------------------------------------------------

function looksLikeGenericOptionLabel(s: string | undefined | null): boolean {
  if (s == null) return true
  const t = s.trim()
  if (t === '') return true
  // e.g. option1, option_1, Option 2, opt1
  return /^option[_\s-]?\d+$/i.test(t) || /^opt[_\s]?\d+$/i.test(t)
}

function firstNonPlaceholderLabel(
  candidates: (string | undefined | null)[],
): string | null {
  for (const c of candidates) {
    const s = typeof c === 'string' ? c.trim() : ''
    if (s && !looksLikeGenericOptionLabel(s)) return s
  }
  return null
}

/**
 * Resolves display strings for both locales. English prefers explicit EN fields
 * (including `label_en`) before `label`, and skips placeholder values like "option1".
 */
function resolveOptionLabelTranslations(
  opt: NonNullable<AggregatedQuestion['options']>[number],
  optIndex: number,
): { en: string; ar: string } {
  const tr = opt.label_translations
  const text = opt.text

  const en = firstNonPlaceholderLabel([
    tr?.en,
    opt.label_en,
    typeof text?.en === 'string' ? text.en : undefined,
    opt.label,
    // Last resort for EN UI: human-readable Arabic beats internal codes
    tr?.ar,
    opt.label_ar,
    typeof text?.ar === 'string' ? text.ar : undefined,
  ]) ?? `Option ${optIndex + 1}`

  const ar = firstNonPlaceholderLabel([
    tr?.ar,
    opt.label_ar,
    typeof text?.ar === 'string' ? text.ar : undefined,
    opt.label,
    tr?.en,
    opt.label_en,
    typeof text?.en === 'string' ? text.en : undefined,
  ]) ?? `خيار ${optIndex + 1}`

  return { en, ar }
}

// ---------------------------------------------------------------------------
// Internal: transform a single AggregatedQuestion into a TransformedQuestion
// ---------------------------------------------------------------------------

function transformSingleQuestion(
  question: AggregatedQuestion,
  index: number,
  logicRules: LogicRule[],
): TransformedQuestion {
  // Map question type - use question_type (primary) or type (legacy)
  const rawType = question.question_type || question.type || 'text'
  const questionType = QUESTION_TYPE_MAP[rawType.toLowerCase()] || rawType

  // Get question text - prioritize text_translations object
  const questionTextEn =
    question.text_translations?.en ||
    question.text ||
    (question as any).text_en ||
    `Question ${index + 1}`

  const questionTextAr =
    question.text_translations?.ar ||
    question.text_ar ||
    (question as any).text_arabic ||
    questionTextEn

  // Transform options if they exist and are not empty
  const options =
    question.options && question.options.length > 0
      ? question.options.map((opt, optIndex) => {
          const labels = resolveOptionLabelTranslations(opt, optIndex)
          return {
            id: String(opt.value ?? optIndex),
            value: String(opt.value ?? optIndex),
            label_translations: labels,
            position: optIndex,
          }
        })
      : undefined

  // Build conditional logic from rules
  const conditionalLogic = logicRules
    .filter((rule) => rule.target_question_id === question.id)
    .map((rule) => ({
      question_id: rule.trigger_question_id,
      operator: rule.condition, // 'equals', 'not_equals', etc.
      value: rule.value,
    }))

  return {
    id: question.aggregated_question_id || question.id || String(index),
    code: question.code || `question_${index}`,
    question_type: questionType,
    position: question.order ?? question.position ?? index,
    page_number: Math.floor(index / 5) + 1, // 5 questions per page
    text_translations: {
      en: questionTextEn,
      ar: questionTextAr,
    },
    validation_rules: {
      required: question.required ?? false,
      pattern: question.validation_rules?.pattern || null,
    },
    options,
    conditional_logic: conditionalLogic.length > 0 ? conditionalLogic : null,
  }
}

// ---------------------------------------------------------------------------
// organizeQuestions — groups by General → Appointment → Service
// ---------------------------------------------------------------------------

/**
 * Takes the raw sorted aggregated questions, transforms them, and assigns
 * section metadata on the first question of each group.
 *
 * Ordering: General (aggregatable / no applies_to) first, then each
 * appointment group sorted by target_id, with service sub-groups.
 *
 * If there is only one appointment, appointment-level headers are omitted
 * (only service headers are shown).
 */
function organizeQuestions(
  sortedAggregated: AggregatedQuestion[],
  logicRules: LogicRule[],
  appointments?: AppointmentContext[],
): TransformedQuestion[] {
  // Separate general vs scoped questions
  const generalRaw: AggregatedQuestion[] = []
  const scopedRaw: AggregatedQuestion[] = []

  for (const q of sortedAggregated) {
    const isGeneral =
      q.aggregatable === true ||
      !q.applies_to ||
      q.applies_to.length === 0
    if (isGeneral) {
      generalRaw.push(q)
    } else {
      scopedRaw.push(q)
    }
  }

  // Build appointment-keyed lookup for scheduled dates
  const appointmentMap = new Map<string, AppointmentContext>()
  if (appointments) {
    appointments.forEach((a) => appointmentMap.set(a.appointmentSpecId, a))
  }

  // Group scoped questions: appointment target_id → scope (service name) → questions
  const appointmentGroups = new Map<
    string,
    Map<string, AggregatedQuestion[]>
  >()

  for (const q of scopedRaw) {
    // Use first applies_to entry for grouping
    const appliesTo = q.applies_to![0]
    const targetId = appliesTo.target_id
    const scope = appliesTo.scope || 'service'

    if (!appointmentGroups.has(targetId)) {
      appointmentGroups.set(targetId, new Map())
    }
    const serviceMap = appointmentGroups.get(targetId)!
    if (!serviceMap.has(scope)) {
      serviceMap.set(scope, [])
    }
    serviceMap.get(scope)!.push(q)
  }

  const multipleAppointments = appointmentGroups.size > 1

  // --- Build the final flat list with section metadata ---
  const result: TransformedQuestion[] = []
  let globalIndex = 0

  // 1) General questions
  if (generalRaw.length > 0) {
    for (let i = 0; i < generalRaw.length; i++) {
      const tq = transformSingleQuestion(generalRaw[i], globalIndex, logicRules)

      // Mark first question of general section
      if (i === 0) {
        tq.section_type = 'general'
        tq.section_title_translations = {
          en: 'General Questions',
          ar: 'أسئلة عامة',
        }
      }

      result.push(tq)
      globalIndex++
    }
  }

  // 2) Scoped questions — per appointment, per service
  let appointmentNumber = 0
  const sortedTargetIds = Array.from(appointmentGroups.keys()).sort()

  for (const targetId of sortedTargetIds) {
    appointmentNumber++
    const serviceMap = appointmentGroups.get(targetId)!
    const appointmentCtx = appointmentMap.get(targetId)
    let isFirstInAppointment = true

    const sortedScopes = Array.from(serviceMap.keys()).sort()

    for (const scope of sortedScopes) {
      const questions = serviceMap.get(scope)!

      for (let i = 0; i < questions.length; i++) {
        const tq = transformSingleQuestion(questions[i], globalIndex, logicRules)

        // First question of each service sub-group gets section metadata
        if (i === 0) {
          tq.section_type = 'service'
          tq.section_title_translations = {
            en: scope,
            ar: scope, // backend scope is typically the service name
          }

          if (appointmentCtx?.scheduledStart) {
            tq.appointment_scheduled_at = appointmentCtx.scheduledStart
          }

          // Appointment-level header on the very first question of this appointment
          if (isFirstInAppointment && multipleAppointments) {
            tq.appointment_section_number = appointmentNumber
          }
          isFirstInAppointment = false
        }

        result.push(tq)
        globalIndex++
      }
    }
  }

  // Edge case: no scoped questions — only general. Already handled above.
  // Edge case: no general and no scoped — return empty.

  return result
}

// ---------------------------------------------------------------------------
// transformEvaluationSnapshot
// ---------------------------------------------------------------------------

export function transformEvaluationSnapshot(
  snapshot: EvaluationSnapshot,
  appointments?: AppointmentContext[],
): SurveyConfig {
  const { aggregated_questions, logic_rules } = snapshot.snapshot_data

  // Extract survey name from various possible fields
  const surveyNameEn =
    snapshot.name_en ||
    snapshot.name ||
    snapshot.title_en ||
    snapshot.title ||
    snapshot.survey_name ||
    snapshot.template_name ||
    snapshot.snapshot_data?.name_en ||
    snapshot.snapshot_data?.name ||
    snapshot.snapshot_data?.title ||
    snapshot.snapshot_data?.survey_name ||
    'Assessment Questions'

  const surveyNameAr =
    snapshot.name_ar ||
    snapshot.title_ar ||
    snapshot.snapshot_data?.name_ar ||
    surveyNameEn ||
    'أسئلة التقييم'

  // Sort by 'order' field (primary) or 'position' (fallback)
  const sorted = [...aggregated_questions].sort(
    (a, b) => (a.order ?? a.position ?? 0) - (b.order ?? b.position ?? 0),
  )

  // Organize into sections (general → appointment → service) with metadata
  const questions = organizeQuestions(sorted, logic_rules, appointments)

  return {
    version: 1,
    template_id: snapshot.id,
    survey_type: 'assessment',
    show_progress: true,
    allow_page_navigation: true,
    name_translations: {
      en: surveyNameEn,
      ar: surveyNameAr,
    },
    questions,
  }
}

// ---------------------------------------------------------------------------
// getVisibleQuestions — conditional logic filtering
// ---------------------------------------------------------------------------

/**
 * Filters the flat question list to only those whose conditional_logic
 * rules are satisfied by the current answers.
 *
 * Rules use question **id** as the key into `answers`.
 * Operators: equals, not_equals, contains, not_empty.
 * ALL rules on a question must pass for it to be visible.
 *
 * Questions with no conditional_logic are always visible.
 */
export function getVisibleQuestions(
  questions: TransformedQuestion[],
  answers: Record<string, any>,
): TransformedQuestion[] {
  return questions.filter((q) => {
    if (!q.conditional_logic || q.conditional_logic.length === 0) return true

    return q.conditional_logic.every((rule) => {
      const currentValue = answers[rule.question_id]

      switch (rule.operator) {
        case 'equals':
          return currentValue === rule.value
        case 'not_equals':
          return currentValue !== rule.value
        case 'not_empty':
          return (
            currentValue !== undefined &&
            currentValue !== null &&
            currentValue !== ''
          )
        case 'contains':
          if (Array.isArray(currentValue)) {
            return currentValue.includes(rule.value)
          }
          return String(currentValue ?? '').includes(String(rule.value))
        default:
          // Unknown operator — treat as visible (safe fallback)
          return true
      }
    })
  })
}

// ---------------------------------------------------------------------------
// serializeAnswersForApi — location cleanup
// ---------------------------------------------------------------------------

/**
 * Round a number to 4 decimal places (~11 m precision).
 */
function roundTo4Decimals(n: number): number {
  return Math.round(n * 1e4) / 1e4
}

/**
 * Rewrites location answers from the UI shape to the API shape.
 *
 * UI shapes accepted:
 *   - { lat, lng, ... }           (LocationPicker flat)
 *   - { coordinates: { lat, lng }, ... }  (nested)
 *
 * API shape produced: { lat: number, lng: number } with 4-decimal precision.
 *
 * All other answers pass through unchanged.
 *
 * NOTE: answers are keyed by question **id** (not code) at this stage.
 * Use this BEFORE mapAnswersToCodes().
 */
export function serializeAnswersForApi(
  answers: Record<string, any>,
  surveyConfig: SurveyConfig,
): Record<string, any> {
  // Build a set of question IDs that are location type
  const locationIds = new Set(
    surveyConfig.questions
      .filter((q) => q.question_type === 'location')
      .map((q) => q.id),
  )

  const out: Record<string, any> = {}

  for (const [key, value] of Object.entries(answers)) {
    if (locationIds.has(key) && value != null && typeof value === 'object') {
      const v = value as Record<string, any>

      // Read lat/lng — flat first, then nested coordinates
      const lat =
        typeof v.lat === 'number'
          ? v.lat
          : (v.coordinates as { lat?: number } | undefined)?.lat
      const lng =
        typeof v.lng === 'number'
          ? v.lng
          : (v.coordinates as { lng?: number } | undefined)?.lng

      if (typeof lat === 'number' && typeof lng === 'number') {
        out[key] = {
          lat: roundTo4Decimals(lat),
          lng: roundTo4Decimals(lng),
        }
      } else {
        // Invalid location — pass through unchanged
        out[key] = value
      }
    } else {
      out[key] = value
    }
  }

  return out
}

// ---------------------------------------------------------------------------
// mapAnswersToCodes — question ID → code for API submission
// ---------------------------------------------------------------------------

/**
 * Helper to map question IDs to codes for API submission.
 */
export function mapAnswersToCodes(
  answers: Record<string, any>,
  questions: TransformedQuestion[],
): Record<string, any> {
  const codeMap: Record<string, string> = {}
  questions.forEach((q) => {
    codeMap[q.id] = q.code
  })

  const mappedAnswers: Record<string, any> = {}
  Object.entries(answers).forEach(([questionId, value]) => {
    const code = codeMap[questionId]
    if (code) {
      mappedAnswers[code] = value
    }
  })

  return mappedAnswers
}
